<template>
  <div
    id="drop-here-wrapper"
    :class="type">
    <div
      v-if="((activeConversation && !emptyObject(activeConversation)) || force) && $refs.fileUpload && $refs.fileUpload.dropActive"
      class="drop-border-wrapper"
    >
      <div
        class="drop-zone"
        :class="{hover: dragHover}"
        @dragenter.stop="dropHover($event, true)"
        @dragleave.stop="dropHover($event, false)">
        <span
          v-if="type !== 'basic'"
          class="drop-image" />
        <h4>{{ type === 'basic' ? 'Drop here' : 'Drop files here' }}</h4>
      </div>
    </div>
    <file-upload
      v-if="!preventFileDrop"
      ref="fileUpload"
      v-model="selectedFiles"
      :multiple="true"
      :drop="true"
      :size="16 * 1024 * 1024"
      @input="addMedia" />
  </div>
</template>

<script>
import FileUpload from 'vue-upload-component';
import { mapGetters } from 'vuex';

import { checkAcceptableFileType } from '@/helpers/lists/whatsappAllowedMediaFiles';
import { getFileExtension } from '@/helpers/mediaHelpers';

export default {
  name: 'DropHere',
  components: {
    FileUpload,
  },
  props: {
    // basic, full
    type: {
      type: String,
      required: false,
      default: 'full',
    },
    force: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      preventFileDrop: false,
      selectedFiles: [],
      unsupportedFiles: [],
      dragHover: false,
      messageToReply: {},
    };
  },
  computed: {
    ...mapGetters({
      activeConversation: 'chat/activeConversation',
    }),
  },
  mounted() {
    this.$eventBus.$on('start-reply', () => { this.messageToReply = { ...this.messageToReply, ...{ [this.activeConversation.id]: true } }; });
    this.$eventBus.$on('cancel-reply-to', () => { this.messageToReply[this.activeConversation.id] = false; });
    this.$eventBus.$on('addoutboundmessage', () => { this.messageToReply[this.activeConversation.id] = false; });
    document.addEventListener('dragover', this.handleDragover, false);
    document.addEventListener('drop', this.handleDrop, false);
    this.$root.$on('bv::modal::hide', () => {
      this.unsupportedFiles = [];
    });

    this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
      // Only prevent file drop if modal other than image-paste-modal is open
      if (modalId !== 'image-paste-modal') {
        this.preventFileDrop = true;
      }
    });
    this.$root.$on('bv::modal::hidden', (bvEvent, modalId) => {
      this.preventFileDrop = false;
    });
  },
  methods: {
    // In order for drop to work we need to implement dragover
    handleDragover(e) {
      e.preventDefault();
    },
    handleDrop(e) {
      e.preventDefault();
    },
    /**
     * Handles media files drop.
     *
     * @param allMedia Dropped media files.
     */
    addMedia(allMedia) {
      if (!this.force && !this.check24HourWindow()) {
        this.$eventBus.$emit('pillmessage', 'WhatsApp 24 hours messaging window expired. Please send a Message Template.');
        this.selectedFiles = [];
        return;
      }
      if (!this.force && this.messageToReply[this.activeConversation.id]) {
        this.$eventBus.$emit('pillmessage', 'Media replies are not supported at the moment.');
        this.selectedFiles = [];
        return;
      }

      const supportedMedia = [];
      const unsupportedMedia = [];

      allMedia.forEach((media) => {
        const fileExtension = getFileExtension(media.name);
        const fileIsAcceptable = checkAcceptableFileType(fileExtension);

        if (fileIsAcceptable) {
          this.$eventBus.$emit('mediaDrop', media);
          supportedMedia.push(media);
        } else {
          unsupportedMedia.push(media);
        }
      });

      this.$eventBus.$emit('dropStats', {
        supportedMedia,
        unsupportedMedia,
      });
      this.selectedFiles = [];
    },
    /** Checks if 24 hours have passed and prevents drop if it did. */
    // TODO --- This should not be checked here because it blocks usage on any other place other than messages.
    check24HourWindow() {
      if (this.activeConversation && !this.emptyObject(this.activeConversation)) {
        const lastInboundDate = this.activeConversation.inbound_sent_at;
        return !(!lastInboundDate || this.$moment().diff(this.$moment(false, false).unix(lastInboundDate), 'days') > 0);
      }
      return false;
    },
    dropHover(e, state) {
      e.stopPropagation();
      e.stopImmediatePropagation();
      this.dragHover = state;
    },
  },
};
</script>

<style lang="scss" scoped>
.drop-border-wrapper {
  padding: 0;
}

#drop-here-wrapper {
  height: 0;
  .drop-zone {
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    top: 0;
    left: 1px;
    right: 1px;
    color: $bluePrimary;
    background-color: rgba($white, 0.85);
    font-size: 30px;
    text-align: center;
    z-index: 5000;

    .drop-image {
      background-image: url(~@/assets/img/SB-Images/drop-background.svg);
      width: 210px;
      height: 167px;
      display: block;
      background-size: contain;
      background-repeat: no-repeat;
    }

    h4 {
      font-size: 18px;
      font-weight: 500;
      line-height: 2.22;
      color: $blackPrimary;
    }
  }

  &.basic {
    .drop-border-wrapper {
      position: absolute;
      padding: 5px;
      background-image: url(~@/assets/img/SB-Icons/border-checker.svg);
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 1px;
      background-size: 12px;
      border-radius: 5px;
      background-color: white;
    }

    .drop-zone {
      border-radius: inherit;
      background-color: rgba($white, 1);
      left: 1px;
      right: 1px;
      top: 1px;
      bottom: 1px;
      height: unset;

      &.hover {
        background-color: $greyUltralight;
      }
    }

    h4 {
      font-size: 14px;
      color: $bluePrimary;
      padding: 20px;
      line-height: 1.3;
    }
  }
}
</style>
